import { when } from 'mobx';
import { useEffect, useState } from 'react';

import type { AggregatedStats, Goals } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import type { TDateRange } from '../ReportsConversionsDashboardPage.utils';
import { formatShare, type IGoalRow } from './ConversionsByGoalTable.utils';

export interface IUseGoalRowsParams {
  range: TDateRange;
}

export interface IUseGoalRowsReturn {
  rows: IGoalRow[];
  isLoading: boolean;
  hasError: boolean;
}

interface IFetchResult {
  rows: IGoalRow[];
  hasError: boolean;
}

/**
 * Reads the account-aggregate endpoint's `top_goals` field
 * (`anhinga/app/controllers/stats/aggregation/account.py`).
 *
 * BE constraints documented so a future contributor doesn't try to "fix" them
 * locally:
 * - `top_goals` is hard-capped at 3 entries server-side, so goals with zero
 *   conversions in the window never appear (AC item "zero-conversion rows
 *   visible but muted" can't be satisfied without a BE change).
 * - Each entry is `{goal_id, count}` only — no per-goal value, no per-goal
 *   campaign count, so the "Conversion Value" and "Campaigns" columns are
 *   deferred until BE adds those fields.
 * - The match stage hardcodes `attribution.full.campaign`, so `state.attr` has
 *   no effect at this endpoint and is not threaded into the request.
 */
async function fetchGoalRows(
  AggregatedStats: AggregatedStats,
  Goals: Goals,
  range: TDateRange,
): Promise<IFetchResult> {
  try {
    const stats = AggregatedStats.aggregate({
      filters: {
        metadata__date__gte: range[0],
        metadata__date__lte: range[1],
      },
      model: 'account',
    });
    await when(() => !stats.isPending);
    if (stats.isErrored) {
      return { rows: [], hasError: true };
    }

    const data = stats.toJS();
    const topGoals = data.top_goals ?? [];
    const totalConversions = data.num_conversions ?? 0;

    if (topGoals.length === 0) {
      return { rows: [], hasError: false };
    }

    const pairs = topGoals.map((entry) => ({
      entry,
      goal: Goals.get(entry.goal_id),
    }));
    await Promise.all(pairs.map(({ goal }) => when(() => !goal.isPending)));

    const built = pairs
      .filter(({ goal }) => !goal.isErrored)
      .map(({ entry, goal }): IGoalRow => {
        const count = entry.count ?? 0;
        return {
          goalId: goal.id,
          name: goal.get('name') ?? '',
          type: goal.get('conv_type'),
          numConversions: count,
          sharePercent: formatShare(count, totalConversions),
        };
      });

    if (built.length === 0) {
      return { rows: [], hasError: true };
    }

    return { rows: built, hasError: false };
  } catch {
    return { rows: [], hasError: true };
  }
}

/**
 * Returns the per-goal aggregate rows for the dashboard's current date range.
 * Late responses from a previous range are discarded via a `cancelled` flag so
 * a fast preset change doesn't race in stale data.
 */
export function useGoalRows({ range }: IUseGoalRowsParams): IUseGoalRowsReturn {
  const { AggregatedStats, Goals } = useStore();
  const [rows, setRows] = useState<IGoalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [start, end] = range;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    void fetchGoalRows(AggregatedStats, Goals, [start, end]).then((result) => {
      if (cancelled) {
        return;
      }
      setRows(result.rows);
      setHasError(result.hasError);
      setIsLoading(false);
    });

    return (): void => {
      cancelled = true;
    };
  }, [start, end]);

  return { rows, isLoading, hasError };
}
